export type HeatmapCell = {
    x: number;
    y: number;
    value: number;
};

const HEATMAP_MIN = -5;
const HEATMAP_MAX = 5;
const HEATMAP_BUCKETS = [-5, -3, -1, 1, 3, 5] as const;

function clamp(v: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, v));
}

function resolveBucket(value: number): number {
    const n = clamp(value, HEATMAP_MIN, HEATMAP_MAX);
    if (n <= -4) return -5;
    if (n <= -2) return -3;
    if (n < 0) return -1;
    if (n <= 2) return 1;
    if (n <= 4) return 3;
    return 5;
}

function bucketAlpha(bucket: number): number {
    if (Math.abs(bucket) === 1) return 0.22;
    if (Math.abs(bucket) === 3) return 0.5;
    return 0.78;
}

function bucketColor(bucket: number): string {
    return bucket >= 0 ? '#15b57a' : '#d94736';
}

function drawLegend(ctx: CanvasRenderingContext2D, width: number, height: number): number {
    if (width < 180 || height < 70) return 0;

    const legendHeight = 18;
    const legendGap = 8;
    const boxW = Math.max(14, Math.floor((width - 20) / HEATMAP_BUCKETS.length));
    const y = height - legendHeight - 2;

    ctx.save();
    ctx.font = '10px "IBM Plex Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const startX = 10;
    HEATMAP_BUCKETS.forEach((bucket, idx) => {
        const x = startX + (idx * boxW);
        const alpha = bucketAlpha(bucket);
        ctx.fillStyle = bucket >= 0
            ? `rgba(21,181,122,${alpha})`
            : `rgba(217,71,54,${alpha})`;
        ctx.fillRect(x, y, Math.max(10, boxW - 2), legendHeight - 8);
        ctx.strokeStyle = 'rgba(255,255,255,0.16)';
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, Math.max(10, boxW - 2), legendHeight - 8);

        ctx.fillStyle = 'rgba(229,231,235,0.88)';
        const label = bucket > 0 ? `+${bucket}` : `${bucket}`;
        ctx.fillText(label, x + Math.max(10, boxW - 2) / 2, y + legendHeight - 2);
    });

    ctx.fillStyle = 'rgba(156,163,175,0.9)';
    ctx.fillText('Heatmap scale', width - 58, y - legendGap + 2);
    ctx.restore();

    return legendHeight + legendGap;
}

export function renderHeatmapGrid(
    canvas: HTMLCanvasElement,
    cells: HeatmapCell[],
    cols: number,
    rows: number,
): void {
    const rect = canvas.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width));
    const height = Math.max(1, Math.floor(rect.height));
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const legendOffset = drawLegend(ctx, width, height);

    const gridHeight = Math.max(1, height - legendOffset);
    const cellW = width / cols;
    const cellH = gridHeight / rows;

    // Dual encoding standard: sign by hue, magnitude by opacity and stroke emphasis.
    const midStroke = 'rgba(255,255,255,0.18)';
    const highStroke = 'rgba(255,255,255,0.34)';

    for (const cell of cells) {
        const bucket = resolveBucket(cell.value);
        const alpha = bucketAlpha(bucket);
        const color = bucketColor(bucket);
        const x = cell.x * cellW;
        const y = cell.y * cellH;
        const w = Math.max(1, cellW - 1);
        const h = Math.max(1, cellH - 1);

        const rgb = bucket >= 0 ? '21,181,122' : '217,71,54';
        ctx.fillStyle = `rgba(${rgb},${alpha})`;
        ctx.fillRect(x, y, w, h);

        if (Math.abs(bucket) >= 3) {
            ctx.strokeStyle = Math.abs(bucket) >= 5 ? highStroke : midStroke;
            ctx.lineWidth = 1;
            ctx.strokeRect(x + 0.5, y + 0.5, Math.max(0, w - 1), Math.max(0, h - 1));
        }

        void color;
    }
}
